import React, { useState, useEffect } from 'react';
import { ordersAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const Orders = () => {
  const { t } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await ordersAPI.getAll();
      setOrders(response.data);
    } catch (error) {
      toast.error(t('toast.failedToLoadOrders'), { autoClose: 1000 });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="My Orders - Order History"
        description="View your complete motorcycle parts order history, track shipments, and manage returns."
        keywords="orders, order history, track order, purchase history"
        noindex={true}
      />

      <div className="container-custom py-12">
        <h1 className="text-3xl font-display font-bold mb-8">{t('orders.title')}</h1>

        {orders.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-xl text-gray-600">{t('orders.noOrders')}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <div key={order.id} className="card p-6">
                <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
                  <div>
                    <h3 className="text-xl font-bold">{t('orders.orderNumber')}{order.order_number}</h3>
                    <p className="text-gray-600 text-sm">
                      {t('orders.placedOn')} {new Date(order.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`px-4 py-2 rounded-lg font-semibold capitalize ${getStatusColor(order.status)}`}>
                    {t(`orders.status${order.status.charAt(0).toUpperCase() + order.status.slice(1)}`)}
                  </span>
                </div>

                <div className="border-t pt-4 space-y-3">
                  {order.order_items.map((item) => (
                    <div key={item.id} className="flex justify-between items-center">
                      <div>
                        <p className="font-semibold">{item.product.name}</p>
                        <p className="text-sm text-gray-600">{t('orders.quantity')}: {item.quantity}</p>
                      </div>
                      <p className="font-semibold">${(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                </div>

                <div className="border-t mt-4 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">{t('orders.total')}</span>
                    <span className="text-2xl font-bold text-racing-red">
                      ${order.total_amount.toFixed(2)}
                    </span>
                  </div>
                </div>

                <div className="mt-4 text-sm text-gray-600">
                  <p><strong>{t('orders.shippingTo')}</strong></p>
                  <p>{order.customer_name}</p>
                  <p>{order.shipping_address}</p>
                  <p>{order.shipping_city}, {order.shipping_state} {order.shipping_zip}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default Orders;
